import React, { useState, useEffect, useCallback } from 'react';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import usePagination from '../utils/usePagination';
import { formatLocalDateTime } from '../utils/formatDateTime';
import '../assets/AllTabsCommon.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';

const ROWS_PER_PAGE = 10;

const COMPLETED_COLUMNS = [
  { key: 'sessionId', label: 'Session ID' },
  { key: 'systemId', label: 'System ID' },
  { key: 'startTime', label: 'Start Time' },
  { key: 'endTime', label: 'End Time' },
  { key: 'duration', label: 'Duration' },
  { key: 'termination', label: 'Termination' },
  { key: 'configVersion', label: 'Config Version' },
  { key: 'actionCount', label: 'Actions' },
];

const TYPE_ROW_CLASS = { monitor: 'rt-action-row-monitor', configure: 'rt-action-row-configure', run: 'rt-action-row-run' };
const ACTION_ICONS = { monitor: '👁️', configure: '⚙️', run: '⚡' };

function RTCompletedSessions() {
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSessionId, setExpandedSessionId] = useState(null);

  const load = useCallback(async () => {
    const data = await realTimeDeviceService.getCompletedSessions();
    setSessions(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = sessions.filter((s) =>
    (s.systemId || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const { pageItems, renderPageNumbers } = usePagination(filtered, currentPage, setCurrentPage, ROWS_PER_PAGE);

  const handleRowClick = (session) => {
    setExpandedSessionId(expandedSessionId === session.sessionId ? null : session.sessionId);
  };

  const renderCell = (session, colKey) => {
    switch (colKey) {
      case 'sessionId':
        return (
          <span
            className="clickable-session-id"
            onClick={() => handleRowClick(session)}
            style={{ cursor: 'pointer', color: '#1a4fba', textDecoration: 'underline' }}
          >
            {session.sessionId}
          </span>
        );
      case 'startTime':
        return formatLocalDateTime(session.startTime);
      case 'endTime':
        return formatLocalDateTime(session.endTime);
      case 'duration':
        return session.durationMins ? `${session.durationMins} min` : '—';
      case 'termination':
        return session.termination || '—';
      case 'configVersion':
        return session.configVersion != null
          ? <span className="rt-chip rt-chip-version">v{session.configVersion}</span>
          : <span className="rt-text-muted">—</span>;
      case 'version':
        return <span className="rt-chip rt-chip-version">{session.version}</span>;
      default:
        return session[colKey];
    }
  };

  return (
    <div className="data-table-tab-container">
      <div className="rt-page-header" style={{ alignItems: 'center', marginBottom: 16 }}>
        <div className="search-container" style={{ margin: 0, flex: 1, maxWidth: 320 }}>
          <input
            type="text"
            placeholder="Search by System ID"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
      </div>

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>{COMPLETED_COLUMNS.map((col) => <th key={col.key}>{col.label}</th>)}</tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr><td colSpan={COMPLETED_COLUMNS.length} className="table-empty">No completed sessions</td></tr>
            ) : (
              pageItems.map((session) => (
                <React.Fragment key={session.sessionId}>
                  <tr className={expandedSessionId === session.sessionId ? 'expanded-row' : ''}>
                    {COMPLETED_COLUMNS.map((col) => <td key={col.key}>{renderCell(session, col.key)}</td>)}
                  </tr>
                  {expandedSessionId === session.sessionId && (
                    <tr className="expanded-details-row">
                      <td colSpan={COMPLETED_COLUMNS.length}>
                        <div className="expanded-row-content">
                          <div className="expanded-details-cards expanded-details-row-1">
                            <div className="expanded-detail-card">
                              <h4>Session Info</h4>
                              <div className="expanded-detail-card-content">
                                <div className="expanded-detail-item"><strong>Session ID:</strong> {session.sessionId}</div>
                                <div className="expanded-detail-item"><strong>System ID:</strong> {session.systemId}</div>
                                <div className="expanded-detail-item"><strong>Duration:</strong> {session.durationMins ? `${session.durationMins} min` : '—'}</div>
                                <div className="expanded-detail-item"><strong>Start Time:</strong> {formatLocalDateTime(session.startTime)}</div>
                                <div className="expanded-detail-item"><strong>End Time:</strong> {formatLocalDateTime(session.endTime)}</div>
                                <div className="expanded-detail-item"><strong>Termination:</strong> {session.termination || '—'}</div>
                                <div className="expanded-detail-item"><strong>Created By:</strong> {session.createdUser || '—'}</div>
                                {session.configVersion != null && (
                                  <div className="expanded-detail-item"><strong>Config Version:</strong> v{session.configVersion}</div>
                                )}
                              </div>
                            </div>
                            <div className="expanded-detail-card">
                              <h4>Configured Actions ({(session.actions || []).length})</h4>
                              <div className="expanded-detail-card-content">
                                {(session.actions || []).length === 0 ? (
                                  <span className="rt-text-muted">No actions were configured.</span>
                                ) : (
                                  <div className="rt-configured-actions">
                                    {(session.actions || []).map((a, idx) => (
                                      <div key={a.id} className={`rt-action-item ${TYPE_ROW_CLASS[a.type] || ''}`}>
                                        <span className={`rt-action-icon ${a.type}`}>{a.icon || ACTION_ICONS[a.type]}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div className="rt-action-label">{a.label} <span className="rt-text-muted" style={{ fontWeight: 400 }}>#{idx + 1}</span></div>
                                          <div className="rt-action-sub">Type: {a.type} · {a.sub || a.key}</div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-controls">{renderPageNumbers()}</div>
    </div>
  );
}

export default RTCompletedSessions;
